import { FC, useMemo, useState, useEffect } from "react";
import clsx from "classnames";
import { useQuery } from "@apollo/client/react/hooks/useQuery";
import { Query } from "@onmo/graphql/graphql";
import { featuredCardsAaaGql } from "../../../graphql";
import { useStyles } from "./ConsoleDesktopContent.style";
import { ConsoleCardStatus } from "../ConsoleCardStatus";
import { ConsoleDeviceCard } from "../ConsoleDeviceCard/ConsoleDeviceCard";
import { ConsoleActivityCard } from "../ConsoleActivityCard/ConsoleActivityCard";
import { ConsoleServiceCard } from "../ConsoleServiceCard";
import { FeaturedConsole } from "../FeaturedConsole";
import PairControllerPopup from "../PairControllerPopup/PairControllerPopup";
import DownloadPopup from "../DownloadPopup/DownloadPopup";
import {
  ConsoleDesktopId,
  FeatureDesktopGroup,
} from "../../../constants/console";
import { getFeatureCardsMap } from "../../../models/console";
import ActivateQrPopup from "../ActivateQrPopup/ActivateQrPopup";
import { AnalyticsGA4 } from "../../../models/analyticsGA4";
import { useGamepadContext } from "../../../contexts/GamepadProvider";
import { ExitAppDialog } from "../ExitAppDialog";
import { isTV } from "../../../utils/platform";
import { useIsKidProfile } from "../../../hooks/useProfileSession";

interface ConsoleDesktopContentProps {
  totalTimePlayed: number;
  totalPlayedGames: number;
}

const ConsoleDesktopContent: FC<ConsoleDesktopContentProps> = ({
  totalTimePlayed,
  totalPlayedGames,
}) => {
  const classes = useStyles();
  const isKid = useIsKidProfile();
  const [openPairController, setOpenPairController] = useState(false);
  const [openDownloadPopup, setOpenDownloadPopup] = useState(false);
  const [activateQRLink, setActivateQRLink] = useState("");
  const [isExitApp, setIsExitApp] = useState(false);
  const { data: featuredCardsData } = useQuery<Query>(featuredCardsAaaGql, {
    variables: { input: { group: FeatureDesktopGroup } },
  });
  const { setCallback, cleanup, setActiveElement } = useGamepadContext();

  const featureCards = useMemo(
    () => getFeatureCardsMap(featuredCardsData, ConsoleDesktopId),
    [featuredCardsData],
  );

  const handleOpenPairController = () => {
    setOpenPairController(true);
    AnalyticsGA4.homepageAction("controller");
  };

  useEffect(() => {
    if (!isTV) return;
    setCallback({
      onBackAction: (activeElement) => {
        const dataGamepad = activeElement?.getAttribute("data-gamepad");
        const activeMenu = dataGamepad?.startsWith("menu-");
        const menuButton = document.querySelector(
          '[data-gamepad="menu-/"]',
        ) as HTMLElement;
        if (activeMenu) {
          setIsExitApp(true);
        } else {
          setActiveElement(menuButton);
        }
      },
    });
    return () => {
      cleanup();
    };
  }, [setCallback, cleanup, setActiveElement]);

  return (
    <>
      <ActivateQrPopup
        onClose={() => setActivateQRLink("")}
        activateLink={activateQRLink}
      />
      <PairControllerPopup
        open={openPairController}
        onClose={() => setOpenPairController(false)}
      />
      <DownloadPopup
        open={openDownloadPopup}
        onClose={() => setOpenDownloadPopup(false)}
      />
      <div className={classes.contentRoot}>
        <div
          className={clsx(classes.consoleContent, classes.consoleContentLeft)}
        >
          <ConsoleCardStatus
            className={classes.consoleCardDesktop}
            setActivateQRLink={setActivateQRLink}
          />
          <ConsoleDeviceCard
            onClick={handleOpenPairController}
            className={classes.consoleCardDesktop}
          />
          <ConsoleActivityCard
            totalTimePlayed={totalTimePlayed}
            totalPlayedGames={totalPlayedGames}
            className={clsx(classes.consoleCardDesktop, classes.activityCard)}
          />
        </div>
        <div className={classes.consoleContent}>
          {featureCards?.["byog_card"] && !isKid && (
            <ConsoleServiceCard
              type="install"
              data={featureCards["byog_card"]}
              className={classes.serviceCard}
            />
          )}
          {featureCards?.["play_anywhere_card"] && (
            <ConsoleServiceCard
              type="download"
              onClick={() => setOpenDownloadPopup(true)}
              data={featureCards["play_anywhere_card"]}
              className={classes.serviceCard}
            />
          )}
        </div>
        {featureCards?.["featured_card"] && (
          <FeaturedConsole
            className={clsx(classes.consoleContent, classes.featuredCard)}
            data={featureCards["featured_card"]}
          />
        )}
      </div>
      <ExitAppDialog open={isExitApp} onClose={() => setIsExitApp(false)} />
    </>
  );
};

export default ConsoleDesktopContent;
